import { useEffect, useState, FC, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Trans } from 'react-i18next'
import { parseISO } from 'date-fns'
import { SelectInstance } from 'react-select'
import { FormatOptionLabelMeta } from 'react-select/dist/declarations/src/Select'

import { Format } from 'services/Dates'
import { getEventFlags, getStartEndFromTimeDate } from 'services/schedule'
import { getDefaultOptionsForMultiSelect, getParsedFlaggedUsersForMultiSelect, getParsedUserForMultiSelect } from 'services/getParsedFlaggedUsersForMultiSelect'
import { getValidFlags } from 'services/schedule/getEventFlags'
import flagsPermissionsHandler from 'services/schedule/flagsPermissionsHandler'
import { hasAccessToThis } from 'services/Utilities'
import getFilteredOptions from 'services/getFilteredOptions'
import { UserHiringData, UserSpecificRestrictions } from 'Manager/Users/components'
import { MultiSelect } from 'components/Forms'
import { Alert, Button } from 'ui'

import { t } from 'translations'

const UserInputWithFlags: FC<{ event: MData.Event & { procedureId: string } }> = ({ event }) => {
  const { users, usersById } = useSelector((state: MStore.RootState) => state.resources)
  const [filterByAttributesFirst, setFilterByAttributesFirst] = useState<boolean>(true)
  const [loadingFlags, setLoadingFlags] = useState<boolean>(true)
  const [flaggedUsers, setFlaggedUsers] = useState<MData.UserWithFlags[]>([])
  const [errorLoadingFlags, setErrorLoadingFlags] = useState(false)

  type Option = { isHidden?: boolean; label: string; alt?: string; options?: Option[] } & Record<string, unknown>

  const selectRef = useRef<SelectInstance<Option> | null>(null)

  const { _id, startTimezone, endTimezone, positionId, projects, team, tags, requiredAttributes, breaks, procedureId } = event
  const [start, end] = getStartEndFromTimeDate(event)

  const currentUser = () => {
    if (event.employeeId && event.employeeId.length && usersById[event.employeeId]) {
      const user = usersById[event.employeeId]
      return [getParsedUserForMultiSelect(user)]
    } else return []
  }

  const multiSelectOptions = (loadingFlags || errorLoadingFlags ? users : flaggedUsers).map(getParsedUserForMultiSelect)

  // Function to custom format the user label in the edit modal
  // Used to add all the flags info
  const userLabel = (flaggedUser: MData.UserWithFlags & { label: string }, meta: FormatOptionLabelMeta<MData.UserWithFlags>) => {
    // If we are rendering only the value with the menu closed
    if (meta.context === 'value') return flaggedUser.label

    if (typeof flaggedUser.flags === 'undefined') return flaggedUser.label

    const {
      label,
      numberOfHoursPerRange,
      flags,
      sortData: { totalProjectExperience = 0, totalRelatedProjectExperience = 0 } = {},
      hiringData = [],
      specificRestrictions = []
    } = flaggedUser

    const shiftFlags = flags.shiftFlags[event._id]

    // Otherwise render all flag options
    return (
      <span>
        <b>
          {label}
          {shiftFlags.isEligible ? <i className="fa fa-check ml-1 text-success" /> : <i className="fa fa-times ml-1 text-danger" />}
        </b>
        {hiringData.length > 0 ? (
          <span className="font-italic ml-1">
            (<UserHiringData hiringData={hiringData} canBeNull />)
          </span>
        ) : null}
        {/* Number of hours in period */}
        <div>
          {(numberOfHoursPerRange ?? []).map(({ numberOfHours, maxHours, range: { start, end } }, index) => (
            <div key={index}>
              <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.maxNumberOfHours_DETAIL">
                <b>
                  {{ numberOfHours }}
                  {{ maxHours }}
                </b>
                <i>
                  {{
                    hoursText: `${Format(parseISO(start), { outFormat: 'humanDateCompact' })} - ${Format(parseISO(end), {
                      outFormat: 'humanDateCompact'
                    })}`
                  }}
                </i>
              </Trans>
            </div>
          ))}
        </div>
        <div>
          <UserSpecificRestrictions specificRestrictions={specificRestrictions} canBeNull />
        </div>
        <div>
          {!loadingFlags
            ? getValidFlags(shiftFlags)
                .map(flag => t(`manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.${flagsPermissionsHandler(flag as MData.UserFlags)}`))
                .join(', ')
            : null}
        </div>
        {/* Number of hours since last shift and to next shift */}
        <div className="font-italic">
          {[
            shiftFlags.reasons.distancePreviousShift[0]
              ? t(`manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.distancePreviousShift_DETAIL`, {
                  distancePreviousShift: Math.floor(shiftFlags.reasons.distancePreviousShift[0] / 60)
                })
              : null,
            shiftFlags.reasons.distanceNextShift[0]
              ? t(`manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.distanceNextShift_DETAIL`, {
                  distanceNextShift: Math.floor(shiftFlags.reasons.distanceNextShift[0] / 60)
                })
              : null
          ]
            .filter(Boolean)
            .join(', ')}
        </div>
        {/* Project experience */}
        {hasAccessToThis('projectSee') ? (
          <div className="font-italic">
            {[
              totalProjectExperience
                ? t(`manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.totalProjectExperience_DETAIL`, {
                    totalProjectExperience: Math.floor(totalProjectExperience / 60)
                  })
                : null,
              totalRelatedProjectExperience
                ? t(`manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.totalRelatedProjectExperience_DETAIL`, {
                    totalRelatedProjectExperience: Math.floor(totalRelatedProjectExperience / 60)
                  })
                : null
            ]
              .filter(Boolean)
              .join(', ')}
          </div>
        ) : null}
        {shiftFlags.reasons.noAvailability ? <div className="text-warning">{t('manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.NO_AVAILABILITY')}</div> : null}
        {shiftFlags.reasons.userMightBeOnHoliday ? (
          <div className="text-warning">{t('manager.schedule.EDIT_MODAL.USER_FLAGS_SHORT.userMightBeOnHoliday')}</div>
        ) : null}
      </span>
    )
  }

  useEffect(() => {
    const getEventFlagsUseEffect = async () => {
      setLoadingFlags(true)
      const { flaggedUsers = [], success } = await getEventFlags(
        { _id, start, startTimezone, end, endTimezone, positionId, projects, team, requiredAttributes, tags, breaks, procedureId },
        false,
        {
          filterByAttributesFirst
        }
      )

      setFlaggedUsers(flaggedUsers)
      if (!success) setErrorLoadingFlags(true)

      setLoadingFlags(false)
    }
    if (start && end && positionId && positionId !== 'noProject') getEventFlagsUseEffect()
  }, [_id, start, startTimezone, end, endTimezone, positionId, requiredAttributes, projects, team, tags, breaks, filterByAttributesFirst, procedureId])

  return (
    <>
      {errorLoadingFlags ? <Alert variant="danger">{t('manager.schedule.eventForm.ERROR_LOADING_FLAGS')}</Alert> : null}
      <MultiSelect
        ref={selectRef}
        module="manager.schedule.eventForm"
        name="employeeId"
        key="user-input-with-flags"
        noOptionsCustomElement={
          filterByAttributesFirst ? (
            <Button
              isLink
              onClick={() => {
                setFilterByAttributesFirst(false)
                selectRef.current?.blur()
                selectRef.current?.openMenu('first')
                selectRef.current?.focus()
              }}
            >
              {t('manager.schedule.EDIT_MODAL.LOAD_UNQUALIFIED_USERS')}
            </Button>
          ) : null
        }
        loadOptions={async (input: string) => {
          const filteredOptions = getFilteredOptions({ userInput: input, type: 'users', sortedOptions: multiSelectOptions })
          if (loadingFlags) return filteredOptions
          return getParsedFlaggedUsersForMultiSelect(filteredOptions, event)
        }}
        options={currentUser()}
        defaultOptions={loadingFlags ? [] : getDefaultOptionsForMultiSelect(multiSelectOptions, event)}
        isClearable
        isLoading={loadingFlags}
        formatOptionLabel={!loadingFlags ? userLabel : null}
        async
        disabled={!hasAccessToThis('scheduleAssignEmployee')}
      />
    </>
  )
}

export default UserInputWithFlags
